import React from 'react';
import { useDispatch } from 'react-redux';
import * as Survey from 'survey-react';
import { PanelModel, ReactSurveyModel } from 'survey-react';
import cx from 'classnames';

import {
  apiUpdateScriptResponse,
  setHasQualifyAnswer,
  setIsOnScript,
  setScriptResponseLeadStatus,
  setScriptResponseUnqualifiedReason,
} from 'state/script.state';
import {
  LeadStatus,
  ScriptLeadStatusActions,
  ScriptResponse,
  ScriptResponseSlim,
  ScriptStepMap,
  ScriptSummaryItem,
  UnqualifyReason,
  UnqualifyReasonGroups,
} from 'typings';

import 'survey-react/survey.css';
import 'assets/styles/survey.scss';
import { getAutoSelectQuestion } from './index';

// register functions for use in SurveyJS expressions
Survey.FunctionFactory.Instance.register('NumericValidator', function (params: any[]) {
  const value = params[0] || null;

  if (typeof value === 'number') return true;
  if (typeof value === 'string') return !!value.match(/^\$?\d{1,3}(\d*|(,\d{3})*)(\.\d+)?$/);

  return false;
});

interface ScriptProps {
  data: ScriptResponse;
  unqualifyReasons: UnqualifyReason[];
  style: 'light' | 'dark';
  onStepRender?: (
    step: string,
    isCurrentStep: boolean,
    script: ReactSurveyModel,
    options: { panel?: PanelModel }
  ) => void;
}

interface SummaryProps {
  data: ScriptSummaryItem[];
  styles?: {
    container: string;
  };
}

const Summary = ({ data, styles }: SummaryProps) => {
  const summary: ScriptSummaryItem[] = data.length ? data.filter((item) => item.answer) : [];

  return (
    <div className={cx('script-summary-view', styles?.container)}>
      {summary.length ? (
        <ul className="script-summary-list">
          {summary.map((item: ScriptSummaryItem) => (
            <li className="script-summary-item">
              <div className="script-summary-item-bubble">
                <div className="script-summary-question">{item.question}</div>
                <div className="script-summary-answer">{item.answer}</div>
              </div>

              <div className="script-summary-pointer-shadow" />
              <div className="script-summary-pointer-bubble">
                <i className="script-summary-pointer-icon material-icons">keyboard_arrow_down</i>
                <i className="script-summary-complete-icon material-icons">check</i>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="script-summary-list">Lead was closed with no script questions answered</div>
      )}
    </div>
  );
};

const Script = ({ data, unqualifyReasons, style = 'light', onStepRender }: ScriptProps) => {
  const dispatch = useDispatch();

  const { answers, currentStepName, scriptCatalog, scriptStepsByName, summary } = data;

  const styles = {
    survey: {
      root: 'sv_main root-main',
      body: 'sv_body container-body',
      panel: {
        container: style === 'dark' ? 'panel-container panel-container-dark' : 'panel-container',
        content: 'panel-content',
        row: 'panel-row',
      },
      row: 'survey-row',
      question: {
        mainRoot: 'sv_qstn survey-question',
      },
      error: {
        root: 'question-error',
      },
    },
    summary: {
      container: style === 'dark' ? 'script-summary-view-dark' : '',
    },
  };

  if (!scriptCatalog) {
    if (!summary?.list) return null;

    return <Summary data={summary.list} styles={styles.summary} />;
  }

  // workaround for an issue with how our implementation interacts with Survey JS
  // ---
  // when this component executes, it will generate a new survey model object to render;
  // subsequent executions of this same component will generate a new survey model object, but by default, will not re-render the HTML elements
  // ---
  // this would result in our custom event handlers we've added to the HTML still referencing the old survey model,
  // causing issues with how these event handlers update the script

  let scriptResponseUpdateQueue: any[] = [];
  let scriptUpdateInProgress = false;
  let queueTimeout: any = null;
  let lastCopy: any;

  // this component doesn't re-render when the script response entity changes
  const id = Math.random().toString();

  // create a local copy of the script response so the frontend can react to changes
  const localScriptResponse: ScriptResponseSlim = JSON.parse(
    JSON.stringify({
      answers: answers ?? {},
      currentStepName: currentStepName ?? '',
    })
  );

  const surveyModel: any = new Survey.Model({ questions: scriptCatalog.catalog?.questions ?? [] });
  surveyModel.checkErrorsMode = 'onValueChanged';
  surveyModel.questionErrorLocation = 'bottom';
  surveyModel.showQuestionNumbers = 'off';
  surveyModel.showPageTitles = false;

  if (localScriptResponse) {
    surveyModel.data = { ...localScriptResponse.answers };
  }

  // populate "other" options for appropriate radio groups
  for (const question of surveyModel.getAllQuestions()) {
    if (question.getType() !== 'html') {
      let displayValue = question.getDisplayValue();
      const answer = localScriptResponse.answers?.[question.name] ?? '';
      const friendlyName = scriptStepsByName?.[question.name]?.friendlyName ?? '';

      if (question.hasOther && answer) {
        // is this answer one of the known choices?
        let isKnownChoice = false;
        const { choices } = question;

        if (choices) {
          isKnownChoice = choices.filter((choice: any) => choice.value === answer).length > 0;
        }

        // if the question has a value that is not represented by known choices, map the value to be shown as "other"
        if (!isKnownChoice) {
          question.comment = answer;
          question.value = 'other';
          displayValue = answer;
        }
      }

      setScriptAnswerVariable(
        surveyModel,
        question.name,
        friendlyName,
        answer,
        displayValue,
        scriptStepsByName
      );
    }
  }

  // HELPER METHODS
  const executeScriptResponseUpdateQueue = (queue: any[]) => {
    // we have updates in the queue ready to be executed
    if (queue.length) {
      // extract all updates we have in the queue
      const updatedResponses = [...queue];
      // combine all changes into one payload
      const combinedPayload: any = {};

      for (const updatedResponse of updatedResponses) {
        // an updatedResponse is a "key => value" to be updated (e.g. "currentStep => step_1")
        for (const [key, value] of Object.entries(updatedResponse)) {
          // if the value is an object, then merge all keys within each of the objects
          if (typeof value === 'object' && value !== null) {
            combinedPayload[key] = {
              ...(combinedPayload[key] || {}),
              ...value,
            };
            // if the value is not an object, set it directly
          } else {
            combinedPayload[key] = value;
          }
        }
      }

      dispatch(apiUpdateScriptResponse(combinedPayload));
    }
  };

  const copyScriptQuestion = (question: any, currentStep: string | null) => {
    // when title is empty (valid for some questions), SurveyJS returns the question name rather than an empty string;
    // we'd prefer an empty string
    const message = question.processedTitle !== question.name ? question.processedTitle : '';

    const scriptCopyEvent = new CustomEvent('scriptCopy', {
      detail: {
        message,
      },
    });

    document.dispatchEvent(scriptCopyEvent);
    dispatch(
      setIsOnScript({
        isOnScript: currentStep !== null,
        onScriptStep: currentStep,
      })
    );
  };

  /**
   * Coordinate an update to the script response
   */
  const updateScriptResponse = (payload: any) => {
    // add this update to the queue
    scriptResponseUpdateQueue.push(payload);

    // if there is no script update in progress, trigger one
    if (!scriptUpdateInProgress) {
      scriptUpdateInProgress = true;

      if (queueTimeout) {
        clearTimeout(queueTimeout);
      }

      // allow time for several consecutive updates to collect so we can send all at once
      queueTimeout = setTimeout(() => {
        executeScriptResponseUpdateQueue(scriptResponseUpdateQueue);
        scriptUpdateInProgress = false;
        scriptResponseUpdateQueue = [];
      }, 10);
    }
  };

  const updateAnswer = (survey: any, stepName: string, answer: any, displayAnswer: any = null) => {
    // These values would override intent; we will need to handle these somehow to handle loading back in
    // Maybe restructure answer payload to allow for display value, to then have conditional logic for survey creation
    if (answer === 'other' || answer === 'Answer' || answer === 'Skip') {
      return;
    }

    const friendlyName = scriptStepsByName?.[stepName]?.friendlyName ?? '';

    localScriptResponse.answers[stepName] = answer;

    setScriptAnswerVariable(
      survey,
      stepName,
      friendlyName,
      answer,
      displayAnswer,
      scriptStepsByName
    );

    updateScriptResponse({
      answers: { [stepName]: answer },
    });
  };

  // SURVEY JS EVENT HANDLER DEFINITIONS

  /**
   * Will be executed when a panel changes from hidden to shown and vice versa
   */
  const onPanelVisibleChanged = (survey: any, options: any) => {
    // panel names are: panel-stepName
    const stepName = options.panel?.name?.replace(/^panel-/, '');

    if (!stepName) return;

    if (!options.visible) {
      // the panel is being hidden; clear the value to empty string and exit
      survey.setValue(stepName, '');
      return;
    }

    // the panel is being shown;
    // set the current step in the script to the new panel
    updateScriptResponse({ currentStepName: stepName });
    localScriptResponse.currentStepName = stepName;

    // TODO: find another way to identify the copy button
    const copyButton = options.panel.questions.findLast(
      (question: any) => question.getType() === 'html' && question.html === '<h6>&#8592; COPY</h6>'
    );

    // copy the message of the new current step
    if (copyButton) {
      // store and invoke copy function, we may call this again later;
      // only auto-copy questions that are not already answered
      lastCopy = () => !copyButton.value && copyScriptQuestion(copyButton, stepName);
      lastCopy();
    }

    // invoke custom callback for new current step
    if (onStepRender) {
      onStepRender(stepName, true, survey, options);
    }

    // determine if this step has an autofill mapping
    const mapping = scriptStepsByName?.[stepName]?.mapping;

    if (mapping !== undefined) {
      let answer = mapping;
      // we have to handle scriptSummary mappings differently, so check for those first
      if (answer?.includes('{scriptSummary[')) {
        const friendlyStepName = answer.slice(16, -3);
        answer = survey.getVariable(`scriptSummary['${friendlyStepName}']`)?.toLowerCase();
      } else {
        answer = answer?.trim()?.toLowerCase();
      }
      // attempt to autofill the answer using the mapping
      const inputField = options.panel.questions.find(
        (question: any) => question.getType() !== 'html'
      );

      if (answer) {
        if (inputField?.choices?.find((choice: any) => choice.value === answer)) {
          // the mapping is one of our known choices, we can autofill
          survey.setValue(stepName, answer);
        } else if (inputField?.hasOther) {
          // the mapping is not one of our known choices; save the answer
          updateAnswer(survey, stepName, answer);

          // then register as "other"; this doesn't save anything on the backend because "other" is not saved
          survey.setValue(stepName, 'other');
          survey.setComment(stepName, answer);
        }
      } else {
        if (inputField?.choices?.find((choice: any) => choice.value === 'next')) {
          // if the mapping is empty, use "next" to attempt to skip the step if possible
          survey.setValue(stepName, 'next');
        }
      }
    }
  };

  /**
   * Will be executed when the value of any question has changed
   */
  const onValueChanged = (survey: any, options: any) => {
    // every panel has three questions: statement (html), question (form control), and copy (html);
    // this event will fire once for each of them, but we only want to perform the following logic on the form control
    if (options.question.getType() === 'html') {
      return;
    }

    updateAnswer(survey, options.name, options.value || '', options.question.getDisplayValue());

    // after we've updated the answer to a question,
    // trigger another copy to ensure we've copied the most updated version of the question
    if (lastCopy) lastCopy();

    // auto-select dropdown options for statuses below
    switch (options.value) {
      case ScriptLeadStatusActions.QUALIFY:
        dispatch(setHasQualifyAnswer(true));
        dispatch(setScriptResponseLeadStatus(LeadStatus.QUALIFIED));
        break;

      case ScriptLeadStatusActions.UNQUALIFY:
        dispatch(setScriptResponseLeadStatus(LeadStatus.UNQUALIFIED));

        // auto-set unqualify reason
        let reason;

        // if there is a specific reason coded into this step, choose it
        const stepReasonId = scriptStepsByName?.[options.name]?.updateOptions?.unqualifyReasonId;

        if (stepReasonId) {
          reason = unqualifyReasons.find((r) => r.id === stepReasonId);
        }

        // otherwise, choose a default
        if (!reason) {
          reason = unqualifyReasons.find(
            (r) => r.unqualifyReasonGroupId === UnqualifyReasonGroups.NOT_INTERESTED
          );
        }

        if (reason) {
          dispatch(setScriptResponseUnqualifiedReason(reason.name));
        }
        break;

      case null:
        const autoSelectQuestion = getAutoSelectQuestion([options.question]);

        // Auto-select question answer has been cleared by the user
        if (autoSelectQuestion) {
          dispatch(setHasQualifyAnswer(false));
          dispatch(setScriptResponseLeadStatus(LeadStatus.NEW_LEAD));
        }
        break;

      default:
        break;
    }

    // this is done in order to trigger re-render for onUpdatePanelCssClasses method
    survey.render(); // FIXME: search for way to re-render only panel due to possible performance issues
  };

  // will be executed after each new panel is rendered
  const onAfterRenderPanel = (
    survey: ReactSurveyModel,
    options: { htmlElement: any; panel: PanelModel }
  ) => {
    const panel = options.htmlElement;

    // stop us from re-rendering the same panel multiple times
    if (panel.dataset.rendered) {
      return;
    }

    const values = options.panel.getValue();
    const name = options.panel.name.replace(/^panel-/, '');

    // add icons to each panel
    const pointerShadow = document.createElement('div');
    pointerShadow.classList.add('pointer-shadow');

    const pointerBubble = document.createElement('div');
    pointerBubble.classList.add('pointer-bubble');

    const pointerIcon = document.createElement('i');
    pointerIcon.classList.add('material-icons');
    pointerIcon.classList.add('pointer-icon');
    pointerIcon.innerText = 'keyboard_arrow_down';

    pointerBubble.appendChild(pointerIcon);

    const completeIcon = document.createElement('i');
    completeIcon.classList.add('material-icons');
    completeIcon.classList.add('complete-icon');
    completeIcon.innerText = 'check';

    pointerBubble.appendChild(completeIcon);

    // when icon is clicked, expand and contract the panel
    pointerBubble.addEventListener('click', () => {
      if (onStepRender) {
        if (!panel.classList.contains('panel-container-expanded')) {
          // panel is expanded (i.e. the step is rendered)
          // panel name is also the name of the step
          onStepRender(name, true, survey, options);
        } else {
          // panel is contracted, equate this to mean that the current step is rendered
          onStepRender(localScriptResponse.currentStepName, true, survey, options);
        }
      }

      panel.classList.remove('panel-container-animate-answer');
      panel.classList.toggle('panel-container-expanded');
    });

    // when the question value changes, contract the panel
    panel.addEventListener('change', () => {
      panel.classList.remove('panel-container-expanded');
      panel.classList.add('panel-container-animate-answer');

      // remove animation class after a couple of seconds so it doesn't animate again if re-rendered
      setTimeout(() => {
        if (panel) {
          panel.classList.remove('panel-container-animate-answer');
        }
      }, 2000);
    });

    panel.appendChild(pointerShadow);
    panel.appendChild(pointerBubble);

    // when a new panel is rendered that is not answered, scroll to that position
    if (!values || !values[name] || getAutoSelectQuestion(options.panel.elements)) {
      panel.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }

    // mark the panel as rendered
    panel.dataset.rendered = true;
  };

  // will be executed after each new question is rendered
  const onAfterRenderQuestion = (survey: any, options: any) => {
    const question = options.htmlElement;

    if (question) {
      // Since the survey does not have actual buttons
      // A 'button' is simply a tag that is EXACTLY <h6>&#8592; COPY</h6>
      // If those are found, register a click event to send the title (message to send)
      // to the message component textarea. This provides a fall back for concierge to re-copy message
      // TODO: find another way to identify the copy button

      if (options.question.html === '<h6>&#8592; COPY</h6>') {
        question.addEventListener('click', () => {
          // if this is the currently-active question being copied, put us on script
          const currentStep =
            options.question.name === localScriptResponse.currentStepName
              ? options.question.name
              : null;

          copyScriptQuestion(options.question, currentStep);
        });
      }

      if (
        ['dropdown', 'radiogroup'].includes(options.question.getType()) &&
        options.question.hasOther
      ) {
        // add a read-only comment box for display when the question is answered
        const commentBox = document.createElement('div');
        commentBox.classList.add('question-comment-box');
        commentBox.innerText = options.question.isOtherSelected ? options.question.comment : '';
        question.appendChild(commentBox);

        // register focus-out events for radio groups with an "other" option
        question.addEventListener('focusout', () => {
          let comment = '';

          // only update the answer if the "other" option is chosen
          if (options.question.isOtherSelected) {
            const hasErrors = options.question.hasErrors(true);

            // if a comment is filled in the box, run validation; if we passed, we can show the comment
            if (options.question.comment && !hasErrors) {
              comment = options.question.comment;
            }

            commentBox.innerText = comment;

            // ensure we have a value so, if not filled in, this option gets selected again on page load
            const answer = comment || '-';
            updateAnswer(survey, options.question.name, answer);

            // "other" is not selected, clear out comment display
          } else {
            commentBox.innerText = '';
          }
        });
      }
    }
  };

  /**
   * Will be executed when an error is displayed to customize the text content
   */
  const onErrorCustomText = (survey: any, options: any) => {
    if (options.name === 'otherempty') options.text = '';
  };

  /**
   * Will be executed when a panel needs to render or re-render
   */
  const onUpdatePanelCssClasses = (survey: any, options: any) => {
    let value;
    let comment;
    let hasErrors = false;

    options.panel.questions.forEach((question: any) => {
      switch (question.getType()) {
        case 'dropdown':
        case 'radiogroup':
          const isOption =
            question.hasOther ||
            question.choices.filter((choice: any) => choice.value === question.value).length > 0;

          // only apply answered styling if the answer is one of the options
          if (isOption) {
            value = question.value;
            comment = question.comment;
            hasErrors = question.hasErrors(false);
          }
          break;

        default:
          break;
      }
    });

    // has the the question in this panel been answered?
    if (value) {
      const containerClasses = options.cssClasses.panel.container.split(' ');

      // add classes to the panel container to indicate answered status
      containerClasses.push('panel-container-filled');

      switch (value) {
        case 'next':
          containerClasses.push('panel-container-skipped');
          break;
        case 'unresponsive':
          containerClasses.push('panel-container-unresponsive');
          break;
        case 'other':
          containerClasses.push('panel-container-complete');

          if (comment && !hasErrors) {
            containerClasses.push('panel-container-comment');
          }
          break;
        default:
          containerClasses.push('panel-container-complete');
          break;
      }

      options.cssClasses.panel.container = containerClasses.join(' ');
    }
  };

  /**
   * Will be executed when a question needs to render or re-render
   */
  const onUpdateQuestionCssClasses = (survey: any, options: any) => {
    switch (options.question.getType()) {
      // dropdown (select fields) get specific classes we're using for styling
      case 'dropdown':
        options.cssClasses.root += ' question-dropdown-fieldset';
        options.cssClasses.control += ' question-dropdown-control';
        options.cssClasses.title += ' question-dropdown-title';
        options.cssClasses.selectWrapper += ' question-dropdown-wrapper';

        // the question has no title, don't leave room for one
        if (!options.question.title.trim()) {
          options.cssClasses.title += ' question-dropdown-title-empty';
        }
        break;

      // radiogroups get specific classes we're using for styling
      case 'radiogroup':
        options.cssClasses.root += ' question-radiogroup-fieldset';
        options.cssClasses.item += ' question-radiogroup-item';
        options.cssClasses.itemChecked += ' question-radiogroup-item-checked';
        options.cssClasses.title += ' question-radiogroup-title';

        // the question has no title, don't leave room for one
        if (!options.question.title.trim()) {
          options.cssClasses.title += ' question-radiogroup-title-empty';
        }
        break;

      // html fields (statement and copy button) get specific classes we're using for styling
      case 'html':
        options.cssClasses.root += ' question-html';

        // the field is empty, don't leave room for it
        if (options.question.html === '<h4></h4>') {
          options.cssClasses.root += ' question-html-empty';
        }
        break;
      default:
        break;
    }
  };

  /**
   * Handler for the event is fired right after survey is rendered in DOM.
   */
  const onAfterRenderSurvey = (survey: any, options: any) => {
    if (onStepRender) {
      for (const [stepName, answer] of Object.entries(localScriptResponse.answers)) {
        if (answer) {
          onStepRender(stepName, false, survey, options);
        }
      }

      onStepRender(localScriptResponse.currentStepName, true, survey, options);
    }
  };

  return (
    <Survey.Survey
      model={surveyModel}
      key={id}
      onPanelVisibleChanged={onPanelVisibleChanged}
      onValueChanged={onValueChanged}
      onAfterRenderPanel={onAfterRenderPanel}
      onAfterRenderQuestion={onAfterRenderQuestion}
      onErrorCustomText={onErrorCustomText}
      onUpdatePanelCssClasses={onUpdatePanelCssClasses}
      onUpdateQuestionCssClasses={onUpdateQuestionCssClasses}
      onAfterRenderSurvey={onAfterRenderSurvey}
      css={styles.survey}
    />
  );
};

/**
 * @param survey
 * @param step - the step for this answer
 * @param friendlyName - the friendly name for this answer
 * @param answer - the underlying answer intent/value
 * @param friendlyAnswer - the value displayed to the user
 * @param scriptStepsByName
 */
const setScriptAnswerVariable = (
  survey: any,
  step: string,
  friendlyName: string,
  answer: string,
  friendlyAnswer: string,
  scriptStepsByName?: ScriptStepMap | null
) => {
  // add a variable for the friendly answer to the question provided, or empty string if the question is unanswered
  const useAnswer =
    answer &&
    !['next', 'unresponsive'].includes(answer) &&
    !new RegExp('^\\([^)]+\\)$').test(friendlyAnswer);

  const finalAnswer = friendlyAnswer || answer;

  survey.setVariable(`scriptAnswers.${step}`, useAnswer ? finalAnswer : '');

  if (!survey.getVariable(`scriptSummary['${friendlyName}']`)) {
    survey.setVariable(`scriptSummary['${friendlyName}']`, useAnswer ? finalAnswer : '');
  } else {
    if (finalAnswer && useAnswer) {
      survey.setVariable(`scriptSummary['${friendlyName}']`, finalAnswer);
    }
  }

  // questions can have step and answer-specific merge fields;
  // e.g. if a question asks for a city, the step can be set up to include the population of the selected city
  // as a merge field that can be used in future questions

  // add variables for each of the step-specific merge fields this question includes based on the answer,
  // or empty strings if the question is unanswered
  const fieldNames = scriptStepsByName?.[step]?.answerMergeFields?.fieldNames || [];
  const fieldValues = scriptStepsByName?.[step]?.answerMergeFields?.fieldValues?.[answer] || {};

  for (const fieldName of fieldNames) {
    // step-specific merge fields take the format: scriptAnswers.STEP_NAME.FIELD_NAME
    survey.setVariable(`scriptAnswers.${step}.${fieldName}`, fieldValues[fieldName] || '');
  }
};

export default Script;
